import React, { useContext, useEffect, useState } from 'react';
import {
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  CircularProgress,
  Backdrop,
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { ROUTE } from 'route/RouteEnums';
import { getProfileStatus } from 'store/slices/userSlice';
import { AuthContext } from 'utils/AuthContext';
import { useAppDispatch } from 'store/hooks';
import { StyledDialog } from 'theme/index';

// 🔁 Update these imports based on your project structure
/* ================= TYPES ================= */

type ProfileStatus = {
  patientInfo: boolean;
  guardianInfo: boolean;
  providerInfo: boolean;
  counselorInfo: boolean;
  pharmacyDetails: boolean;
  insuranceDetails: boolean;
};

/* ================= COMPONENT ================= */

const ProfileCompletionGuard = () => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const { user, setUserDetails } = useContext(AuthContext);
  const [openDialog, setOpenDialog] = useState(false);
  const [profileCheckLoader, setProfileCheckLoader] = useState(false);
  const [dialogMessage, setDialogMessage] = useState('');
  const [status, setStatus] = useState<ProfileStatus | null>(null);

  /* ================= EFFECT ================= */

  useEffect(() => {
    if (user?.patientId) {
      checkProfileStatus(user?.patientId);
    }
  }, [user?.patientId]);

  /* ================= API CALL ================= */

  const checkProfileStatus = (patientId: number) => {
    if (user?.isAdmin) return; // Admin users are not subject to profile completion checks
    setProfileCheckLoader(true);
    try {
      dispatch(getProfileStatus(patientId))
        .unwrap()
        .then(async (response: any) => {
          if (response?.status === 200) {
            const data = response?.data;

            const isIncomplete =
              !data?.patientInfoStatus ||
              !data?.guardianInfoStatus ||
              !data?.childProviderInfoStatus ||
              !data?.childCounselorInfoStatus ||
              !data?.pharmacyInfoStatus ||
              !data?.paymentInfoStatus;

            if (isIncomplete) {
              setDialogMessage('Please complete your profile to continue.');
              setStatus({
                patientInfo: data?.patientInfoStatus,
                guardianInfo: data?.guardianInfoStatus,
                providerInfo: data?.childProviderInfoStatus,
                counselorInfo: data?.childCounselorInfoStatus,
                pharmacyDetails: data?.pharmacyInfoStatus,
                insuranceDetails: data?.paymentInfoStatus,
              });
              setOpenDialog(true);
            }
          }
        });
    } catch (error) {
      console.error('Profile status check failed:', error);
    } finally {
      setProfileCheckLoader(false);
    }
  };

  /* ================= NAVIGATION ================= */

  const handleDialogOk = () => {
    setProfileCheckLoader(false);
    setOpenDialog(false);
    if (!status) return;

    if (!status.patientInfo) {
      navigate(ROUTE.PATIENT_INFORMATION);
    } else if (!status.guardianInfo) {
      navigate(ROUTE.GUARDIAN_INFORMATION);
    } else if (!status.providerInfo) {
      navigate(ROUTE.PROVIDER_INFORMATION);
    } else if (!status.counselorInfo) {
      navigate(ROUTE.COUNSELOR_INFORMATION);
    } else if (!status.pharmacyDetails) {
      navigate(ROUTE.PHARMACY_DETAILS);
    } else {
      navigate(ROUTE.INSURANCE_DETAILS);
    }
  };

  /* ================= RENDER ================= */

  return (
    <>
      <Backdrop open={profileCheckLoader} sx={{ zIndex: 1300 }}>
        <CircularProgress color='inherit' />
      </Backdrop>
      {/* Loader */}

      {/* Dialog */}
      <StyledDialog
        open={openDialog}
        disableEscapeKeyDown
        maxWidth='xs' // ✅ make dialog smaller
        fullWidth
      >
        <DialogTitle
          sx={{
            textAlign: 'center',
            pb: 1,
            fontSize: '1rem',
            fontWeight: 600,
          }}
        >
          Complete Your Profile
        </DialogTitle>

        <DialogContent sx={{ textAlign: 'center', p: 2 }}>
          <Typography sx={{ fontSize: '13px' }}>{dialogMessage}</Typography>
        </DialogContent>

        <DialogActions
          sx={{
            justifyContent: 'center',
            pb: 2,
          }}
        >
          <Button
            variant='contained'
            size='small'
            onClick={handleDialogOk}
            sx={{
              minWidth: 90,
              px: 1.5,
              py: 0.5,
              fontSize: '0.75rem',
              borderRadius: '16px',
              textTransform: 'none',
            }}
          >
            OK
          </Button>
        </DialogActions>
      </StyledDialog>
    </>
  );
};

export default ProfileCompletionGuard;
